import { useState, useEffect } from "react";
import axios from "axios";
import { KAPHY_BASE_URL } from "../service/SignUpService";

export const BUSINESS_DEFAULTS = {
  hours: {
    monday: { open: "09:00", close: "19:00", isClosed: false },
    tuesday: { open: "09:00", close: "19:00", isClosed: false },
    wednesday: { open: "09:00", close: "19:00", isClosed: false },
    thursday: { open: "09:00", close: "19:00", isClosed: false },
    friday: { open: "09:00", close: "19:00", isClosed: false },
    saturday: { open: "13:00", close: "19:00", isClosed: false },
    sunday: { open: "13:00", close: "19:00", isClosed: false },
  },
  address: {
    street: "2910 Pleasant Ave",
    floorSuite: "1st Floor, Suite 31",
    building: "Karmel Mall",
    city: "Minneapolis",
    state: "MN",
    zip: "55408",
  },
};

// Module-level cache — one fetch per session regardless of how many components mount
let _cache = null;
let _promise = null;

function loadSettings() {
  if (_cache) return Promise.resolve(_cache);
  if (_promise) return _promise;
  _promise = axios
    .get(`${KAPHY_BASE_URL}/api/settings`)
    .then((res) => {
      const data = res.data || {};
      const rawHours = data.hours || {};
      _cache = {
        hours: Object.fromEntries(
          Object.entries(BUSINESS_DEFAULTS.hours).map(([day, def]) => [
            day,
            { ...def, ...(rawHours[day] || {}) },
          ]),
        ),
        address: { ...BUSINESS_DEFAULTS.address, ...(data.address || {}) },
      };
      _promise = null;
      return _cache;
    })
    .catch(() => {
      _promise = null;
      return BUSINESS_DEFAULTS;
    });
  return _promise;
}

/** Call after a successful admin save so next consumer re-fetches fresh data. */
export function invalidateBusinessSettingsCache() {
  _cache = null;
  _promise = null;
}

/** "13:00" → "1:00 PM",  "09:00" → "9:00 AM" */
export function formatTime(time24) {
  if (!time24) return "";
  const [hStr, mStr] = time24.split(":");
  let h = parseInt(hStr, 10);
  const ampm = h >= 12 ? "PM" : "AM";
  if (h > 12) h -= 12;
  if (h === 0) h = 12;
  return `${h}:${mStr} ${ampm}`;
}

/** { open: "09:00", close: "19:00", isClosed: false } → "9:00 AM - 7:00 PM" */
export function formatDayHours(dayData) {
  if (!dayData || dayData.isClosed) return "Closed";
  return `${formatTime(dayData.open)} – ${formatTime(dayData.close)}`;
}

/**
 * Returns the full address as a single line:
 * "2910 Pleasant Ave, 1st Floor, Suite 31, Karmel Mall, Minneapolis, MN 55408"
 */
export function formatFullAddress(address) {
  if (!address) return "";
  const parts = [
    address.street,
    address.floorSuite,
    address.building,
    `${address.city}, ${address.state} ${address.zip}`,
  ].filter(Boolean);
  return parts.join(", ");
}

export function useBusinessSettings() {
  const [settings, setSettings] = useState(_cache || BUSINESS_DEFAULTS);
  const [loading, setLoading] = useState(!_cache);

  useEffect(() => {
    if (_cache) {
      setSettings(_cache);
      setLoading(false);
      return;
    }
    setLoading(true);
    loadSettings().then((data) => {
      setSettings(data);
      setLoading(false);
    });
  }, []);

  return { settings, loading };
}
